import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { AlertCircleIcon, ArrowUpRightIcon, Building2Icon, LogOutIcon, User2Icon } from "lucide-react";
import { mobileRoutes } from "./constants/mobileRoutes";
import IconButton from "@/common/components/IconButton";
import { useNavigate } from "react-router-dom";
import Alert from "@/common/components/Alert";
import Skeleton from "@/common/components/Skeleton";
import Button from "@/common/components/Button";
import { useCurrentUser } from "@/common/hooks/useCurrentUser";
import useLogOut from "@/common/hooks/useLogOut";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { useMemo } from "react";

const MobileHome = () => {
  const navigate = useNavigate();

  const {
    data: company,
    isPending: companyIsPending,
  } = useMineCompany();

  const {
    data: currentUser,
    isPending: currentUserIsPending,
  } = useCurrentUser({ });

  const {
    logOut,
    isPending
  } = useLogOut();

  const { userPermissionsSet, isUserPermissionsPending } = useUserPermissions();

  const routesWithPermission = useMemo(() => {
    return mobileRoutes
      .filter(
        (route) =>
          route.permission &&
          userPermissionsSet.size > 0 &&
          userPermissionsSet.has(route.permission),
      );
  }, [isUserPermissionsPending]);

  return (
    <div className="flex flex-col h-dvh w-screen">
      <div className="p-4 flex items-center justify-between">
        <img src="/Logo.svg" className="h-6" />

        <div className="flex items-center gap-2">
          <ImageWithPlaceholder
            placeholderIcon={User2Icon}
            className="size-6"
            iconClassName="size-4"
            url={currentUser?.profile_picture?.url}
          />

          {currentUserIsPending && (
            <Skeleton className="w-20 h-4" />
          )}

          {!currentUserIsPending && (
            <div className="flex flex-col">
              <p className="text-[13px]">
                {currentUser?.full_name}
              </p>

              <div>
                <Button
                  variant="link-white"
                  className="text-danger-soft text-sm text-[13px] gap-1"
                  onClick={() => logOut()}
                  disabled={isPending}
                >
                  <LogOutIcon className="text-danger-soft size-3.5" />

                  Sair
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 py-2 border-t border-b border-white-100">
        {companyIsPending && (
          <div className="flex items-center gap-1.5">
            <Skeleton circle height={24} />

            <Skeleton className="w-24 h-5" />
          </div>
        )}

        {!companyIsPending && (
          <ImageWithDisplayName
            placeholderIcon={Building2Icon}
            imageUrl={company?.general_information?.company_logo?.url}
            iconClassName="size-4"
            imageClassName="size-6"
            displayName={company?.general_information.fantasy_name}
          />
        )}
      </div>

      <div className="h-full overflow-auto p-4 flex flex-col gap-4">
        {isUserPermissionsPending && (
          <div className="flex flex-col gap-3">
            {new Array(3).fill(null).map((_, index) => (
              <Skeleton
                key={`skeleton-${index}`}
                height={68}
                className="rounded-lg"
              />
            ))}
          </div>
        )}

        {Number(routesWithPermission?.length) > 0 && !isUserPermissionsPending && (
          <div className="flex flex-col gap-3">
            {routesWithPermission.map((route, index) => (
              <button
                key={`route-${index}`}
                className="rounded-lg bg-white-200 hover:bg-white-300/25 p-4 flex items-center justify-between"
                onClick={() => navigate(route.path)}
              >
                <div className="flex items-center gap-4">
                  {route.Icon}

                  <p className="font-bold text-lg">
                    {route.label}
                  </p>
                </div>

                <div>
                  <IconButton
                    Icon={<ArrowUpRightIcon size={18} />}
                    className="bg-primary-blue hover:bg-primary-blue-hover size-8"
                  />
                </div>
              </button>
            ))}
          </div>
        )}

        {Number(routesWithPermission.length) === 0 && !isUserPermissionsPending && (
          <div className="w-full flex flex-col items-center justify-center py-6 overflow-auto">
            <img src="/cloud.gif" className="shrink-0 w-64" />

            <div className="flex flex-col items-center -mt-4 mb-4 gap-1.5 px-2">
              <p className="text-sm font-bold text-center">Você não possui permissão para acessar estes recursos.</p>
              <p className="text-xs mt-2 text-center">Entre em contato com seu gestor para mais informações.</p>
            </div>
          </div>
        )}

        <Alert
          variant="warning"
          Icon={AlertCircleIcon}
          iconClassName="min-w-6 size-6"
          containerClassName="items-start p-3 gap-2"
        >
          <div className="flex flex-col gap-0.5">
            <p className="text-xs">
              Atenção
            </p>

            <p className="text-xs font-light italic">
              Essa é uma versão simplificada da plataforma. <br />Acesse por computador ou laptop para conferir todas as funcionalidades.
            </p>
          </div>
        </Alert>
      </div>
    </div>
  );
}

export default MobileHome;
